import { batch } from 'react-redux';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MAX_PAGE_SIZE } from 'dashboard/lib/constants/pagination';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Environment } from 'dashboard/models/environment';
import { InquiryTemplate, InquiryTemplateFiltersResponse } from 'dashboard/models/inquiry-template';
import { Organization } from 'dashboard/models/organization';
import { OrganizationPlan } from 'dashboard/models/organization-plan';
import { Template, TemplateFiltersResponse } from 'dashboard/models/template';
import { Theme } from 'dashboard/models/theme';
import {
  VerificationTemplate,
  VerificationTemplateFiltersResponse,
} from 'dashboard/models/verification-template';
import {
  setCurrentOrganizationPlan,
  setEnvironments,
  setInquiryTemplateFilterOptionsForOrganization,
  setInquiryTemplatesForOrganization,
  setOrganization,
  setTemplateFilterOptionsForOrganization,
  setTemplatesForOrganization,
  setThemes,
  setVerificationTemplateFilterOptionsForOrganization,
  setVerificationTemplatesForOrganization,
} from 'dashboard/redux/actions/organization';
import { VerificationTemplatesFilterParams } from 'dashboard/redux/actions/verification-templates';
import { dynamicFlowTemplatesOnlySelector } from 'dashboard/redux/selectors/inquiry-template';
import { setAndPersistCurrentEnvironment } from 'dashboard/redux/thunk-actions/current-environment';
import { DashboardThunkAction } from 'dashboard/redux/types';
import StorageClient, { LocalStorageKey } from 'lib/clients/StorageClient';
import { ApiEndpoint, ApiModel, ApiSingletonPayload, PaginationParams } from 'lib/interfaces/api';

export const loadOrganization = (): DashboardThunkAction<Promise<void>> => async (dispatch) => {
  const { data: organization, included } =
    await SessionClientDashboard.getResourceSingleton<Organization>(ApiEndpoint.Organization);

  const environments: Environment[] = included
    ? (organization.relationships['environments'].data.map((envData) =>
        included.find((i) => i.id === envData.id),
      ) as Environment[])
    : [];

  const currentOrganizationPlanData = organization.relationships['current-organization-plan']?.data;

  dispatch(setEnvironments(environments));
  dispatch(setOrganization(organization));

  if (currentOrganizationPlanData?.id && included) {
    const currentOrganizationPlan = included.find(
      (i) => i.id === currentOrganizationPlanData.id,
    ) as OrganizationPlan;
    dispatch(setCurrentOrganizationPlan(currentOrganizationPlan));
  }

  // Rely on localStorage selected environment; default to first found if none set
  dispatch(
    setAndPersistCurrentEnvironment(
      environments.find(
        (env) =>
          env.id === StorageClient.getLocalItem(LocalStorageKey.DashboardCurrentEnvironmentId),
      ) || environments[0],
    ),
  );
};

export const updateOrganization =
  (attributes: Partial<Organization['attributes']>): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const { data: organization } =
      await SessionClientDashboard.updateResourceSingleton<Organization>({
        type: ApiModel.Organization,
        attributes,
      });

    dispatch(setOrganization(organization));
  };

// Thunks below update attributes not on the Organization type
export const updateStripePaymentMethod =
  (paymentMethodId: string): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const { data: organization } = await SessionClientDashboard.updateResourceSingleton<
      Organization,
      ApiSingletonPayload<{ 'stripe-payment-method-id': string }, ApiModel.Organization>
    >({
      type: ApiModel.Organization,
      attributes: {
        'stripe-payment-method-id': paymentMethodId,
      },
    });

    dispatch(setOrganization(organization));
  };

export const updateOrgDefaultThemeSet =
  (themeSetId: string): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const { data: organization } = await SessionClientDashboard.updateResourceSingleton<
      Organization,
      ApiSingletonPayload<{ 'theme-set-id': string }, ApiModel.Organization>
    >({
      type: ApiModel.Organization,
      attributes: {
        'theme-set-id': themeSetId,
      },
    });

    dispatch(setOrganization(organization));
  };

export const updateOnboardingChecklistSandboxCompletedSteps =
  (steps: string[]): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const { data: organization } = await SessionClientDashboard.updateResourceSingleton<
      Organization,
      ApiSingletonPayload<
        { 'onboarding-checklist-sandbox-completed-steps': string[] },
        ApiModel.Organization
      >
    >({
      type: ApiModel.Organization,
      attributes: {
        'onboarding-checklist-sandbox-completed-steps': steps,
      },
    });

    dispatch(setOrganization(organization));
  };

export const updateSamlUrl =
  (metadataUrl: string): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const { data: organization } = await SessionClientDashboard.updateResourceSingleton<
      Organization,
      ApiSingletonPayload<{ 'idp-metadata-url': string }, ApiModel.Organization>
    >({
      type: ApiModel.Organization,
      attributes: {
        'idp-metadata-url': metadataUrl,
      },
    });

    dispatch(setOrganization(organization));
  };

export const updateSamlXml =
  (metadataXml: string): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const { data: organization } = await SessionClientDashboard.updateResourceSingleton<
      Organization,
      ApiSingletonPayload<{ 'idp-metadata-xml': string }, ApiModel.Organization>
    >({
      type: ApiModel.Organization,
      attributes: {
        'idp-metadata-xml': metadataXml,
      },
    });

    dispatch(setOrganization(organization));
  };

export const loadThemeList = (): DashboardThunkAction<Promise<void>> => async (dispatch) => {
  const { data: templates } = await SessionClientDashboard.listResource<Theme>(ApiEndpoint.Theme);

  dispatch(setThemes(templates));
};

export const loadVerificationTemplates = (): DashboardThunkAction => async (dispatch) => {
  const { data: verificationTemplates } = await SessionClientDashboard.listResource<
    VerificationTemplate,
    PaginationParams
  >(ApiEndpoint.VerificationTemplate, {
    sort: 'name',
    page: {
      number: 1,
      size: MAX_PAGE_SIZE,
    },
  });

  dispatch(setVerificationTemplatesForOrganization(verificationTemplates));
};

export const loadVerificationTemplateFilterOptions =
  ({
    filters: { omni, status, type },
    sortParams: { sort, shouldSortDescending },
  }: {
    filters: VerificationTemplatesFilterParams;
    sortParams: SortParams;
  }): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const { meta } = await SessionClientDashboard.get<VerificationTemplateFiltersResponse>(
      ApiEndpoint.VerificationTemplate,
      'basics',
      '',
      {
        filter: { omni, status, type },
        sort: `${shouldSortDescending ? '-' : ''}${sort}`,
      },
    );
    dispatch(setVerificationTemplateFilterOptionsForOrganization(meta.filters));
  };

export const loadNewAndLegacyInquiryTemplateList =
  (): DashboardThunkAction<Promise<void>> => async (dispatch, getState) => {
    if (dynamicFlowTemplatesOnlySelector(getState())) {
      dispatch(loadDynamicFlowInquiryTemplateList());
    } else {
      const [{ data: inquiryTemplates }, { data: templates }] = await Promise.all([
        SessionClientDashboard.listResource<InquiryTemplate>(ApiEndpoint.InquiryTemplate),
        SessionClientDashboard.listResource<Template>(ApiEndpoint.Template),
      ]);

      batch(() => {
        dispatch(setInquiryTemplatesForOrganization(inquiryTemplates));
        dispatch(setTemplatesForOrganization(templates));
      });
    }
  };

export const loadDynamicFlowInquiryTemplateList =
  (): DashboardThunkAction<Promise<void>> => async (dispatch) => {
    const { data: inquiryTemplates } = await SessionClientDashboard.listResource<
      InquiryTemplate,
      PaginationParams
    >(ApiEndpoint.InquiryTemplate, {
      sort: 'name',
      page: {
        number: 1,
        size: MAX_PAGE_SIZE,
      },
    });

    dispatch(setInquiryTemplatesForOrganization(inquiryTemplates));
  };

export const loadNewAndLegacyInquiryTemplateFilterOptionList =
  (): DashboardThunkAction<Promise<void>> => async (dispatch, getState) => {
    if (dynamicFlowTemplatesOnlySelector(getState())) {
      const { meta } = await SessionClientDashboard.get<InquiryTemplateFiltersResponse>(
        ApiEndpoint.InquiryTemplate,
        'basics',
        '',
      );

      dispatch(setInquiryTemplateFilterOptionsForOrganization(meta.filters));
    } else {
      const [{ meta: inquiryTemplatesMeta }, { meta: templatesMeta }] = await Promise.all([
        SessionClientDashboard.get<InquiryTemplateFiltersResponse>(
          ApiEndpoint.InquiryTemplate,
          'basics',
          '',
        ),
        SessionClientDashboard.get<TemplateFiltersResponse>(ApiEndpoint.Template, 'basics', ''),
      ]);

      batch(() => {
        dispatch(setInquiryTemplateFilterOptionsForOrganization(inquiryTemplatesMeta.filters));
        dispatch(setTemplateFilterOptionsForOrganization(templatesMeta.filters));
      });
    }
  };
